import type { ReactNode } from 'react';
import { githubVegaAppUrlSlug } from '@gonfalon/dogfood-flags/dogfood-flags';
import type { ButtonProps, PopoverProps } from '@launchpad-ui/components';
import {
  Button,
  Focusable,
  Header,
  Menu as LDMenu,
  MenuItem,
  MenuSection,
  MenuTrigger,
  Popover,
  Tooltip,
  TooltipTrigger,
} from '@launchpad-ui/components';
import { Icon } from '@launchpad-ui/icons';

import { IconSolidGithub } from '../icons';

import styles from './GitHubRepoSelector.module.css';

export interface GitHubRepo {
  key: string;
  name: string;
  is_public: boolean;
}

export interface GitHubRepoSelectorProps {
  repos: GitHubRepo[];
  selectedKeys: string[];
  onSelectionChange: (keys: string[]) => void;
  /** Content rendered above the menu list (e.g. a warning callout). */
  headerContent?: ReactNode;
  /** Popover placement relative to the trigger. Defaults to "bottom start". */
  placement?: PopoverProps['placement'];
  /** Whether the trigger button is disabled. */
  disabled?: boolean;
  /** CSS class applied to the trigger button. */
  triggerClassName?: string;
  /** GitHub icon to render in the trigger. Defaults to the built-in IconSolidGithub. */
  githubIcon?: ReactNode;
  /** Override the default "Manage repositories" action. */
  onManage?: () => void;
  /** Trigger button size. Defaults to "medium". */
  size?: ButtonProps['size'];
}

export function GitHubRepoSelector({
  repos,
  selectedKeys,
  onSelectionChange,
  headerContent,
  placement = 'bottom start',
  disabled,
  triggerClassName,
  githubIcon,
  onManage,
  size = 'medium',
}: GitHubRepoSelectorProps) {
  const slug = githubVegaAppUrlSlug();

  const privateRepos = repos.filter((repo) => !repo.is_public);
  const publicRepos = repos.filter((repo) => repo.is_public);
  const privateRepoKeys = privateRepos.map((repo) => repo.key);

  const effectiveSelected = selectedKeys.filter((key) => privateRepoKeys.includes(key));
  const allSelected = privateRepoKeys.length > 0 && effectiveSelected.length === privateRepoKeys.length;

  const selectedCount = effectiveSelected.length;
  let label: string;
  if (selectedCount === 0) {
    label = 'No repositories';
  } else if (selectedCount === 1) {
    const repo = privateRepos.find((r) => effectiveSelected.includes(r.key));
    const fullName = repo?.name ?? '1 repository';
    label = fullName.includes('/') ? (fullName.split('/').pop() ?? fullName) : fullName;
  } else {
    label = `${selectedCount} repositories`;
  }

  const handleToggleAll = () => {
    onSelectionChange(allSelected ? [] : privateRepoKeys);
  };

  const handleManage = () => {
    if (onManage) {
      onManage();
      return;
    }
    const url = new URL(`https://github.com/apps/${slug}/installations/new`);
    url.searchParams.set('state', window.location.href);
    window.location.href = url.toString();
  };

  const icon = githubIcon ?? <IconSolidGithub size={14} />;

  return (
    <MenuTrigger>
      <Button variant="minimal" size={size} isDisabled={disabled} className={triggerClassName}>
        {icon} {label}
        <Icon name="chevron-down" size="small" />
      </Button>
      <Popover placement={placement}>
        <div className={styles.popoverContent}>
          <div className={styles.headerActions}>
            <Button variant="minimal" size="small" onPress={handleToggleAll}>
              {allSelected ? 'Clear all' : 'Select all'}
            </Button>
            <Button variant="minimal" size="small" onPress={handleManage}>
              Manage repositories <Icon name="link-external" size="small" />
            </Button>
          </div>
          {headerContent}
          <LDMenu
            selectionMode="multiple"
            selectedKeys={effectiveSelected}
            onSelectionChange={(keys) => {
              if (keys === 'all') {
                onSelectionChange(privateRepoKeys);
                return;
              }
              const selected = Array.from(keys);
              onSelectionChange(selected.map(String));
            }}
            style={{ maxHeight: '40vh' }}
            disabledKeys={publicRepos.map((repo) => repo.key)}
          >
            <MenuSection>
              {privateRepos.map((repo) => (
                <MenuItem key={repo.key} id={repo.key}>
                  {repo.name}
                </MenuItem>
              ))}
            </MenuSection>
            {publicRepos.length > 0 && (
              <MenuSection aria-label="Public repositories">
                <Header>Public repositories</Header>
                {publicRepos.map((repo) => (
                  <MenuItem key={repo.key} id={repo.key} textValue={repo.name}>
                    <TooltipTrigger>
                      <Focusable>
                        <span>{repo.name}</span>
                      </Focusable>
                      <Tooltip placement="right">Public repositories are not supported for security reasons</Tooltip>
                    </TooltipTrigger>
                  </MenuItem>
                ))}
              </MenuSection>
            )}
          </LDMenu>
        </div>
      </Popover>
    </MenuTrigger>
  );
}
